import { z } from 'zod';
import { APP_ENVIRONMENTS } from '@tsgecart/config';

/**
 * Validated shape of the Worker's runtime bindings (wrangler vars + secrets).
 * Phase 0 only requires DATABASE_URL and APP_ENV to exist — everything else
 * is optional today and becomes required as later phases wire it up, so a
 * missing future secret never breaks the Phase 0 health check.
 */
const baseWorkerEnvSchema = z.object({
  APP_ENV: z.enum(APP_ENVIRONMENTS),
  DATABASE_URL: z.string().url(),
  CORS_ALLOWED_ORIGINS: z.string().min(1),
  LOG_LEVEL: z.enum(['debug', 'info', 'warn', 'error']).default('info'),

  // Reserved for later phases — intentionally optional until the routes that
  // consume them exist. See docs/auth-environment-variables.md.
  //
  // Clerk (Phase 2 — managed identity/session provider; see docs/authentication-architecture.md):
  // CLERK_SECRET_KEY / CLERK_WEBHOOK_SIGNING_SECRET are Worker secrets, never
  // exposed to the frontend. CLERK_JWT_ISSUER/CLERK_JWT_AUDIENCE are not
  // secret (they're public identifiers embedded in every Clerk JWT) but live
  // here because JWT verification needs them at runtime the same way it
  // needs the secret key. CLERK_PUBLISHABLE_KEY is also not secret — some
  // @clerk/backend call shapes accept it alongside the secret key.
  //
  // Optional at the base-schema level for ALL of APP_ENV — a superRefine
  // below tightens this per-environment (required in preview/production,
  // still optional in development so local/DI'd test runs that never call
  // the real Clerk API aren't forced to configure it). See "Environment
  // validation" in docs/authentication-architecture.md.
  CLERK_SECRET_KEY: z.string().optional(),
  CLERK_PUBLISHABLE_KEY: z.string().optional(),
  CLERK_WEBHOOK_SIGNING_SECRET: z.string().optional(),
  CLERK_JWT_ISSUER: z.string().url().optional(),
  // Optional unconditionally, in every environment — only meaningful if the
  // configured Clerk instance's JWT template actually sets an `aud` claim;
  // plenty of legitimate Clerk configurations have none.
  CLERK_JWT_AUDIENCE: z.string().optional(),
  // Networkless verification key (Clerk dashboard → API keys → Show JWT
  // public key) — preferred over CLERK_SECRET_KEY's JWKS-fetch fallback
  // (Phase 2.4 wires this into apps/worker's Clerk verification middleware).
  CLERK_JWT_KEY: z.string().optional(),

  // Not used by /sign-in, /sign-up, or the Clerk webhook — those are
  // protected by Clerk's own Dashboard-configured bot protection (Cloudflare
  // Turnstile under the hood, applied to Clerk's prebuilt UI automatically).
  // Reserved for a possible future non-Clerk form that needs its own
  // server-side siteverify check. See docs/security-model.md "Turnstile /
  // bot protection".
  TURNSTILE_SECRET_KEY: z.string().optional(),
  // Razorpay (payments milestone): RAZORPAY_KEY_ID is not secret (it is
  // returned to the frontend in the checkout payload so Razorpay
  // Checkout.js can open, the same pattern as a Stripe publishable key),
  // but lives here rather than in env-web.ts because the Worker also
  // needs it for the Basic-auth header on outbound Razorpay API calls.
  // RAZORPAY_KEY_SECRET / RAZORPAY_WEBHOOK_SECRET are Worker secrets,
  // never exposed to the frontend. Optional at the base-schema level for
  // ALL of APP_ENV; superRefine below requires all three in
  // preview/production. See docs/razorpay-architecture.md "Secrets".
  RAZORPAY_KEY_ID: z.string().optional(),
  RAZORPAY_KEY_SECRET: z.string().optional(),
  RAZORPAY_WEBHOOK_SECRET: z.string().optional(),
  GOOGLE_APPS_SCRIPT_URL: z.string().url().optional(),
  GOOGLE_APPS_SCRIPT_SHARED_SECRET: z.string().optional(),

  // Notifications & Email milestone. RESEND_API_KEY is a Worker secret,
  // never exposed to the frontend (no VITE_ equivalent exists or should
  // exist). EMAIL_FROM_ADDRESS/EMAIL_FROM_NAME are not secret but live
  // here because only the Worker ever constructs an outbound email.
  // EMAIL_REPLY_TO is optional in every environment -- omitting it simply
  // means replies go to EMAIL_FROM_ADDRESS, Resend's own default.
  // APP_PUBLIC_URL is a new, dedicated canonical storefront URL used to
  // build absolute links inside emails -- deliberately NOT derived from
  // CORS_ALLOWED_ORIGINS (a comma-separated allowlist meant for CORS
  // matching, not a single canonical URL to embed in customer-facing
  // content) and not duplicating VITE_API_BASE_URL (env-web.ts, the
  // frontend's API origin, a different concern). RESEND_WEBHOOK_SECRET
  // is a Worker secret for POST /api/v1/webhooks/resend signature
  // verification (Svix scheme, see
  // integrations/resend/webhook-verification.ts). Optional at the
  // base-schema level for ALL of APP_ENV; superRefine below requires the
  // required subset in preview/production. See
  // docs/resend-integration.md "Secrets".
  RESEND_API_KEY: z.string().optional(),
  EMAIL_FROM_ADDRESS: z.string().email().optional(),
  EMAIL_FROM_NAME: z.string().optional(),
  EMAIL_REPLY_TO: z.string().email().optional(),
  APP_PUBLIC_URL: z.string().url().optional(),
  RESEND_WEBHOOK_SECRET: z.string().optional(),

  // R2 media storage (see docs/media-r2-storage.md and env.ts's
  // `MEDIA_BUCKET` binding doc comment): the R2 bucket itself has no public
  // URL until either a custom domain or r2.dev public access is configured
  // on it -- an infra step outside this codebase. MEDIA_R2_PUBLIC_BASE_URL
  // is the base to prefix an uploaded object's key with when constructing
  // `media_assets.publicUrl` (e.g. `https://media.tsgecart.com` or an
  // `*.r2.dev` URL, no trailing slash). Optional in every environment,
  // unconditionally -- unlike RESEND_API_KEY/RAZORPAY_KEY_ID above, there is
  // no superRefine requiring this even in production, because the bucket
  // access itself isn't provisioned yet; requiring the env var first would
  // just move the same gap one step earlier without fixing anything. Until
  // it's set, the upload service still writes the object to R2 and the
  // `media_assets` row, but stores `publicUrl: null` rather than inventing
  // a URL that would 404.
  MEDIA_R2_PUBLIC_BASE_URL: z.string().url().optional(),
});

/**
 * Per-environment tightening on top of the base shape. Deliberately
 * `superRefine` rather than a discriminated union: every other field's
 * shape is identical across environments, only "is Clerk config required"
 * changes, and a refine keeps that as a single readable rule instead of
 * duplicating the whole object three times.
 *
 * `preview`/`production`: `CLERK_SECRET_KEY` is required — these are real
 * deployments where `GET /api/v1/auth/me` is live and lazy provisioning
 * can genuinely call Clerk's Backend API (see
 * `apps/worker/src/modules/auth/lib/clerk-user-api.ts`); a deployment
 * missing it would only fail at the moment a brand-new user tries to sign
 * in, which is worse than failing at startup. `CLERK_JWT_ISSUER` is
 * required for the same reason — without it, `resolveClerkVerifyConfig`
 * cannot verify any token at all. `development` keeps both optional: local
 * dev and the test suite exercise Clerk verification against
 * dependency-injected fakes or a locally-generated test keypair (see
 * tests/worker/auth/*.test.ts), never the real Clerk API, so there is
 * nothing to fail fast on.
 *
 * Issue messages name only the missing field, never a value — no secret
 * (or anything else) is ever echoed back into a validation error.
 */
export const workerEnvSchema = baseWorkerEnvSchema.superRefine((value, ctx) => {
  if (value.APP_ENV === 'development') return;

  if (!value.CLERK_SECRET_KEY) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['CLERK_SECRET_KEY'],
      message: `CLERK_SECRET_KEY is required when APP_ENV is "${value.APP_ENV}" — lazy provisioning calls Clerk's Backend API in this environment.`,
    });
  }

  if (!value.CLERK_JWT_ISSUER) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['CLERK_JWT_ISSUER'],
      message: `CLERK_JWT_ISSUER is required when APP_ENV is "${value.APP_ENV}" — Clerk token verification is enabled in this environment.`,
    });
  }

  // Phase 2.5: POST /api/v1/webhooks/clerk is live in every deployed
  // environment — a deployment missing this would silently 500 on every
  // webhook delivery rather than failing at startup.
  if (!value.CLERK_WEBHOOK_SIGNING_SECRET) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['CLERK_WEBHOOK_SIGNING_SECRET'],
      message: `CLERK_WEBHOOK_SIGNING_SECRET is required when APP_ENV is "${value.APP_ENV}" — Clerk webhook processing is enabled in this environment.`,
    });
  }

  // Razorpay payment routes and POST /api/v1/webhooks/razorpay are live in
  // every deployed environment — a deployment missing any of these would
  // silently 500 on the first checkout attempt or webhook delivery rather
  // than failing at startup.
  if (!value.RAZORPAY_KEY_ID) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['RAZORPAY_KEY_ID'],
      message: `RAZORPAY_KEY_ID is required when APP_ENV is "${value.APP_ENV}" — Razorpay checkout is enabled in this environment.`,
    });
  }
  if (!value.RAZORPAY_KEY_SECRET) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['RAZORPAY_KEY_SECRET'],
      message: `RAZORPAY_KEY_SECRET is required when APP_ENV is "${value.APP_ENV}" — Razorpay order creation calls the Razorpay API in this environment.`,
    });
  }
  if (!value.RAZORPAY_WEBHOOK_SECRET) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['RAZORPAY_WEBHOOK_SECRET'],
      message: `RAZORPAY_WEBHOOK_SECRET is required when APP_ENV is "${value.APP_ENV}" — Razorpay webhook processing is enabled in this environment.`,
    });
  }

  // Notifications & Email milestone: transactional email sending and
  // POST /api/v1/webhooks/resend are live in every deployed environment.
  // `development` deliberately stays optional -- the notification job
  // service falls back to a documented mock-send mode when
  // RESEND_API_KEY is absent (see docs/resend-integration.md "Local
  // development behavior"), so local/test runs are never forced to
  // configure a real Resend account.
  if (!value.RESEND_API_KEY) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['RESEND_API_KEY'],
      message: `RESEND_API_KEY is required when APP_ENV is "${value.APP_ENV}" — transactional email sending is enabled in this environment.`,
    });
  }
  if (!value.EMAIL_FROM_ADDRESS) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['EMAIL_FROM_ADDRESS'],
      message: `EMAIL_FROM_ADDRESS is required when APP_ENV is "${value.APP_ENV}" — every outbound email needs a sender address.`,
    });
  }
  if (!value.EMAIL_FROM_NAME) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['EMAIL_FROM_NAME'],
      message: `EMAIL_FROM_NAME is required when APP_ENV is "${value.APP_ENV}" — every outbound email needs a display sender name.`,
    });
  }
  if (!value.APP_PUBLIC_URL) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['APP_PUBLIC_URL'],
      message: `APP_PUBLIC_URL is required when APP_ENV is "${value.APP_ENV}" — email templates build absolute links from it.`,
    });
  }
  if (!value.RESEND_WEBHOOK_SECRET) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['RESEND_WEBHOOK_SECRET'],
      message: `RESEND_WEBHOOK_SECRET is required when APP_ENV is "${value.APP_ENV}" — Resend webhook processing is enabled in this environment.`,
    });
  }
});

export type WorkerEnv = z.infer<typeof baseWorkerEnvSchema>;

export function parseWorkerEnv(raw: unknown): WorkerEnv {
  return workerEnvSchema.parse(raw);
}
